import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ShieldCheck, ShieldAlert, Check, Plus, Camera, LayoutDashboard, ClipboardList, PackagePlus, Boxes } from "lucide-react";
import { toast } from "sonner";
import client, { apiErr } from "@/lib/api";
import useMeta from "@/lib/useMeta";
import { imgUrl } from "@/lib/catalog";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import DashboardShell from "@/components/DashboardShell";
import MerchantOverview from "@/components/MerchantOverview";

const STATUS_LABEL = { pending: "قيد المراجعة", verified: "موثّق", rejected: "مرفوض", suspended: "موقوف" };

export default function Merchant() {
  const nav = useNavigate();
  const meta = useMeta();
  const { user, ready } = useAuth();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ name: "", cat: "electronics", city: "طرابلس", moq: 10, stock: 500, price: 10, image: null });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => client.get("/merchant/me").then((r) => setData(r.data));
  const loadOrders = () => client.get("/merchant/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => {
    if (!ready) return;
    if (user === false) { nav("/auth?next=/merchant"); return; }
    client.get("/merchant/me").then((r) => {
      if (!r.data.merchant) { nav("/seller-signup"); return; }
      setData(r.data);
      loadOrders();
    }).catch(() => nav("/auth?next=/merchant"));
  }, [ready, user]);

  const NEXT_STATUS = [
    { s: "confirmed", l: "تأكيد" },
    { s: "processing", l: "تجهيز" },
    { s: "shipped", l: "شحن" },
    { s: "delivered", l: "تسليم" },
    { s: "cancelled", l: "إلغاء" },
  ];
  const updateOrder = async (orderId, status) => {
    try { await client.put(`/merchant/orders/${orderId}/status`, { status }); toast.success("تم تحديث حالة الطلب"); loadOrders(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8 text-center" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>;

  const m = data.merchant;
  const verified = m.status === "verified";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const goto = (k) => { const el = document.getElementById(`sec-${k}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const navItems = [
    { key: "overview", label: "لوحة التحكم", icon: LayoutDashboard },
    { key: "orders", label: "الطلبات الواردة", icon: ClipboardList, badge: orders.length },
    { key: "add", label: "إضافة منتج", icon: PackagePlus },
    { key: "products", label: "منتجاتي", icon: Boxes },
  ];

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: up.data.url }));
      toast.success("تم رفع الصورة");
    } catch (er) {
      toast.error(apiErr(er.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const publish = async () => {
    if (!form.name.trim()) return toast.error("أدخل اسم المنتج");
    setBusy(true);
    try {
      await client.post("/merchant/products", {
        name: form.name.trim(), cat: form.cat, city: form.city,
        moq: parseInt(form.moq) || 1, stock: parseInt(form.stock) || 1, price: parseFloat(form.price) || 1,
        image: form.image,
      });
      toast.success("تم نشر المنتج");
      setForm((f) => ({ ...f, name: "", image: null }));
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const banner = {
    verified: ["ok", "var(--k-emerald-tint)", "var(--k-emerald)", "حسابك موثّق. منتجاتك ظاهرة للمشترين."],
    pending: ["p", "var(--k-gold-tint)", "var(--k-gold-deep)", "حسابك بانتظار التحقق من فريق كزيوني. لن تظهر منتجاتك للمشترين ولن تتمكن من النشر حتى الموافقة."],
    rejected: ["b", "var(--k-ruby-tint)", "var(--k-ruby)", "تم رفض طلب التوثيق. راجع بياناتك مع الدعم قبل إعادة المحاولة."],
    suspended: ["b", "var(--k-ruby-tint)", "var(--k-ruby)", "تم تعليق حسابك من قبل الإدارة. منتجاتك مخفية عن المشترين حاليًا."],
  }[m.status];

  return (
    <DashboardShell roleLabel="لوحة التاجر" topLabel={m.name} items={navItems} active="overview" onSelect={goto}>
      {verified && <div id="sec-overview"><MerchantOverview onSelect={goto} /></div>}

      <div className="px-4 pt-4 pb-1">
        <p className="font-head text-[15px] font-bold">{m.name}</p>
        <p className="text-[12px]" style={{ color: "var(--k-ink-soft)" }}>{m.city} · {meta.categories.find((c) => c.id === m.cat)?.name || m.cat}</p>
      </div>

      <div data-testid="merchant-banner" className="flex gap-2.5 mx-4 mt-3 p-3.5 rounded-2xl text-[12.5px] leading-relaxed items-start" style={{ background: banner[1], color: banner[2] }}>
        {verified ? <Check size={16} className="mt-0.5 shrink-0" /> : m.status === "pending" ? <ShieldAlert size={16} className="mt-0.5 shrink-0" /> : <ShieldAlert size={16} className="mt-0.5 shrink-0" />}
        <span>{banner[3]}</span>
      </div>

      <div className="flex gap-2.5 px-4 pt-4">
        <div className="flex-1 rounded-2xl p-3.5 text-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <div className="font-num text-[20px] font-bold" data-testid="stat-products">{data.products.length}</div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--k-ink-soft)" }}>منتجاتي</div>
        </div>
        <div className="flex-1 rounded-2xl p-3.5 text-center" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <div className="text-[13px] font-bold pt-1.5" style={{ color: verified ? "var(--k-emerald)" : m.status === "pending" ? "var(--k-gold-deep)" : "var(--k-ruby)" }}>{STATUS_LABEL[m.status]}</div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--k-ink-soft)" }}>حالة الحساب</div>
        </div>
      </div>

      {verified && (
        <>
          <h2 id="sec-orders" className="font-head text-[15px] font-bold px-4 pt-5 pb-2">الطلبات الواردة ({orders.length})</h2>
          {orders.length === 0 ? (
            <p className="px-4 text-[12.5px]" style={{ color: "var(--k-ink-soft)" }}>لا توجد طلبات بعد.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 px-4">
              {orders.map((o) => (
                <div key={o.orderId} data-testid={`m-order-${o.orderId}`} className="rounded-2xl p-3.5" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="font-num font-bold text-[13px]">{o.orderId}</span>
                    <span className="text-[10.5px] font-bold px-2.5 py-1 rounded-full" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>{o.statusLabel}</span>
                  </div>
                  <p className="text-[11.5px] mb-1.5" style={{ color: "var(--k-ink-soft)" }}>{o.payMethodLabel} · التوصيل: {o.destinationCity} · <span className="font-num">{o.phone}</span></p>
                  {o.items.map((i, k) => (
                    <div key={k} className="flex justify-between text-[12px] py-0.5" style={{ color: "var(--k-ink-soft)" }}>
                      <span>{i.name} × {i.qty}</span><span className="font-num">{i.subtotal} د.ل</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-[12px] pt-1.5 mt-1.5" style={{ borderTop: "1px solid var(--k-line)" }}>
                    <span>إجمالي حصتك</span><span className="font-num font-bold">{o.myTotal} د.ل</span>
                  </div>
                  <div className="flex justify-between text-[11px] mt-0.5" style={{ color: "var(--k-ink-soft)" }}>
                    <span>العمولة</span><span className="font-num">-{o.myCommission} د.ل</span>
                  </div>
                  <div className="flex justify-between text-[12px] mt-0.5" style={{ color: "var(--k-emerald)" }}>
                    <span>صافي مستحقاتك</span><span className="font-num font-bold">{o.myNet} د.ل</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {NEXT_STATUS.map((n) => (
                      <button key={n.s} data-testid={`m-order-${o.orderId}-${n.s}`} onClick={() => updateOrder(o.orderId, n.s)} className="k-tap flex-1 min-w-[60px] py-1.5 rounded-lg text-[11px] font-bold" style={{ background: o.status === n.s ? "var(--k-ink)" : "var(--k-paper)", color: o.status === n.s ? "#fff" : (n.s === "cancelled" ? "var(--k-ruby)" : "var(--k-ink)"), border: "1px solid var(--k-line)" }}>{n.l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {verified && (
        <div id="sec-add" className="rounded-2xl mx-4 mt-4 p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <h3 className="font-head text-[14px] font-bold mb-3 flex items-center gap-1.5"><Plus size={16} /> إضافة عرض جملة جديد</h3>
          <label className="text-[12px] font-semibold block mb-1.5">اسم المنتج</label>
          <input data-testid="p-name" value={form.name} onChange={set("name")} placeholder="مثال: أسمنت بورتلاندي 50 كغ" className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-3" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />
          <div className="flex gap-2.5 mb-3">
            <div className="flex-1"><label className="text-[12px] font-semibold block mb-1.5">التصنيف</label>
              <select data-testid="p-cat" value={form.cat} onChange={set("cat")} className="w-full rounded-lg px-2 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
                {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex-1"><label className="text-[12px] font-semibold block mb-1.5">المدينة</label>
              <select data-testid="p-city" value={form.city} onChange={set("city")} className="w-full rounded-lg px-2 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }}>
                {meta.cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2.5 mb-3">
            <div className="flex-1"><label className="text-[12px] font-semibold block mb-1.5">الحد الأدنى</label><input data-testid="p-moq" type="number" min="1" value={form.moq} onChange={set("moq")} className="w-full rounded-lg px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} /></div>
            <div className="flex-1"><label className="text-[12px] font-semibold block mb-1.5">المخزون</label><input data-testid="p-stock" type="number" min="1" value={form.stock} onChange={set("stock")} className="w-full rounded-lg px-3 py-2.5 text-[13px]" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} /></div>
          </div>
          <label className="text-[12px] font-semibold block mb-1.5">السعر الأساسي (د.ل)</label>
          <input data-testid="p-price" type="number" step="0.1" min="0.1" value={form.price} onChange={set("price")} className="w-full rounded-lg px-3 py-2.5 text-[13px] mb-4" style={{ background: "var(--k-paper)", border: "1px solid var(--k-line)" }} />

          <label className="text-[12px] font-semibold block mb-1.5">صورة المنتج</label>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="p-image-input" />
          <button type="button" data-testid="p-image-btn" onClick={() => fileRef.current?.click()} className="k-tap w-full flex items-center justify-center gap-2 py-3 rounded-lg text-[12.5px] mb-4 overflow-hidden" style={{ border: "1px dashed var(--k-line)", color: "var(--k-ink-soft)" }}>
            {form.image ? (
              <img src={imgUrl(form.image)} alt="preview" className="h-16 w-full object-cover rounded" />
            ) : (
              <><Camera size={15} /> {uploading ? "جارٍ الرفع…" : "رفع صورة (اختياري)"}</>
            )}
          </button>

          <button data-testid="publish-btn" disabled={busy} onClick={publish} className="k-tap w-full py-3 rounded-2xl font-bold text-[14px] text-white" style={{ background: "var(--k-ink)", opacity: busy ? 0.7 : 1 }}>نشر المنتج</button>
        </div>
      )}

      {data.products.length > 0 && (
        <>
          <h2 id="sec-products" className="font-head text-[15px] font-bold px-4 pt-5 pb-2">منتجاتي</h2>
          <div className="flex flex-col gap-2.5 px-4 pb-8">
            {data.products.map((p) => <ProductCard key={p.id} p={p} list />)}
          </div>
        </>
      )}
    </DashboardShell>
  );
}
